import { QuestionIcon, XIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { createFormHook, createFormHookContexts, useSelector } from '@tanstack/react-form'
import classNames from 'classnames'
import { useId, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import { DefaultButton } from '../../../components/buttons/default-button.tsx'
import { GhostButton } from '../../../components/buttons/ghost-button.tsx'
import { Disclosure } from '../../../components/disclosure.tsx'
import * as footerStyles from '../../../components/drawers/floating-footer.css.ts'
import { FloatingFooter } from '../../../components/drawers/floating-footer.tsx'
import { RadioGroup, RadioItem } from '../../../components/input/radio.tsx'
import { TextInput } from '../../../components/input/text-input.tsx'
import { useShowMutationError } from '../../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../../services/toast-service.tsx'
import {
  useAllIntegrations,
  useGetAzureManagedIdentities,
  useGetIsPostIntegrationMutating,
  usePostIntegration,
  type PostIntegrationVariables,
} from '../../../utils/api-client/api-hooks.ts'
import { parsePositiveInteger } from '../../../utils/text-utils.ts'
import { DocumentationDrawer } from './documentation-drawer.tsx'
import * as styles from './integration-drawer.css.ts'
import { FormErrorBanner, IntegrationFieldInput, getFieldFormatError } from './integration-field-input.tsx'
import { CatalogBrandIcon } from './integration-icon.tsx'
import type {
  AzureAuthChoice,
  CatalogEntry,
  CreatableIntegrationType,
  IntegrationField,
} from './integration-instance-utils.ts'
import {
  ADVANCED_SECTION_SUMMARY,
  AZURE_AUTH_FIELDS,
  AZURE_MANAGED_IDENTITY_FIELD_KEY,
  getAzureSetupFields,
  getHasConnectionWebhookRoute,
  getIsAzureDevOpsServicesUrl,
  getIsFieldRequired,
  getIsSonarCloudUrl,
  getIsSystemInstance,
  getIsWebhookTool,
  getProviderLabel,
  getToolFields,
  parseWorkItemId,
  reportRoleRefusal,
  toCheckmarxRegion,
} from './integration-instance-utils.ts'
import {
  NAME_FIELD_KEY,
  getFieldErrorText,
  getIntegrationSaveError,
  getIsConflictReportedOnField,
  getIsFieldAnchored,
  type IntegrationSaveError,
} from './integration-validation.ts'
import {
  getAzureWebhookUser,
  getConnectionWebhookUrl,
  getCreatedId,
  getSystemConnectionWebhookNote,
  getWebhookAuthMethod,
  getWebhookUrl,
  type WebhookAuthChoice,
  type WebhookHandoffSubject,
} from './integration-webhook.ts'
import { preventEscapeWhile } from './prevent-escape.ts'
import { returnFocusOnClose } from './return-focus.ts'
import { WebhookHandoff } from './webhook-handoff.tsx'

// #region Types

type WebhookAuthMethod = WebhookAuthChoice

type SavedInstance = {
  name: string
  webhookUrl: string
  subject: WebhookHandoffSubject
}

// The form is a flat name + a per-tool value bag + the AppScan webhook-auth method + the Azure DevOps
// authentication method, plus the step the drawer is on and the last failure. Field state lives in
// the TanStack Form store (not in a component that renders Dialog.Content) so typing never
// re-renders Dialog.Content — that would re-run Radix's FocusScope ref callback and exceed React's
// update-depth limit. The step and the failure live there too because the footer owns the mutation
// and the body renders what it produces.
type SetupFormValues = {
  name: string
  values: Record<string, string>
  webhookMethod: WebhookAuthMethod
  azureAuthMethod: AzureAuthChoice
  step: 'form' | 'handoff'
  savedInstance: SavedInstance | undefined
  saveError: IntegrationSaveError | undefined
}

type SetupDrawerProps = {
  entry: CatalogEntry
  onClose: () => unknown
  opener?: HTMLElement
}

// #endregion

// #region Form hook

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

const FORM_DEFAULT_VALUES: SetupFormValues = {
  name: '',
  values: {},
  webhookMethod: 'secret',
  azureAuthMethod: 'pat',
  step: 'form',
  savedInstance: undefined,
  saveError: undefined,
}

const useSetupForm = (defaultValueOverrides: Partial<SetupFormValues> = {}) =>
  useAppForm({ defaultValues: { ...FORM_DEFAULT_VALUES, ...defaultValueOverrides } })

const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

// #endregion

// #region Helpers

// Seeds any field that declares a default.
const initialValuesFor = (fields: IntegrationField[]): Record<string, string> =>
  Object.fromEntries(fields.map(field => [field.key, field.defaultValue ?? '']))

/** The chooser hands back a generic `string`; this is the one place that narrows it. */
const toWebhookAuthMethod = (value: string): WebhookAuthMethod => (value === 'basic' ? 'basic' : 'secret')

/** The Azure chooser hands back a generic `string` too; anything unrecognized is the default, a PAT. */
const toAzureAuthMethod = (value: string): AzureAuthChoice => (value === 'managed-identity' ? value : 'pat')

/**
 * The fields on screen, with Azure DevOps's chosen method's credentials in place of its token. Both
 * the field list and the footer read this, so the required, format and anchoring checks cover
 * exactly the fields the user sees.
 */
const getSetupFields = ({
  tool,
  baseUri,
  azureAuthMethod,
}: {
  tool: CreatableIntegrationType | undefined
  baseUri: string | undefined
  azureAuthMethod: AzureAuthChoice
}): IntegrationField[] => {
  if (tool === undefined) {
    return []
  }
  return tool === 'azure'
    ? getAzureSetupFields({ baseUri, authChoice: azureAuthMethod })
    : getToolFields({ tool, baseUri })
}

/**
 * Whether Azure DevOps's Setup form offers a choice of method, and the method in effect. Managed
 * identity is offered only when the operator added one and the server is Azure DevOps Services,
 * the only host its token is for. Whenever it is not offered the method in effect is the token, so
 * a choice made while it was offered — before the server changed, or before a refetch found the
 * identities gone — cannot leave the form asking for an identity it no longer shows.
 */
const useAzureAuthChoice = ({ tool, baseUri }: { tool: CreatableIntegrationType | undefined; baseUri?: string }) => {
  const form = useFormContext()
  const chosenMethod = useSelector(form.store, state => state.values.azureAuthMethod)
  const { data } = useGetAzureManagedIdentities({ enabled: tool === 'azure' })
  const managedIdentityClientIds = (data?.identities ?? []).map(identity => identity.client_id)
  const isOffered = tool === 'azure' && managedIdentityClientIds.length > 0 && getIsAzureDevOpsServicesUrl(baseUri)
  const method: AzureAuthChoice = isOffered ? chosenMethod : 'pat'
  return { isOffered, method, managedIdentityClientIds }
}

/** An optional field is omitted from the request when blank, rather than sent as an empty string. */
const omitIfBlank = (value: string | undefined): string | undefined => (value ? value : undefined)

// AppScan submits exactly one webhook-auth method — the platform rejects both or neither with a 400.
const buildCreateBody = (type: CreatableIntegrationType, form: SetupFormValues): PostIntegrationVariables['body'] => {
  const { webhookMethod, azureAuthMethod } = form
  const name = form.name.trim()
  // A pasted key routinely carries a trailing space or newline, and sending it verbatim fails auth
  // with nothing to point at. No credential or URL has meaningful surrounding whitespace.
  const values = Object.fromEntries(Object.entries(form.values).map(([key, value]) => [key, value.trim()]))
  return match(type)
    .with('datadog', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      api_key: values.api_key ?? '',
      application_key: values.application_key ?? '',
    }))
    .with('arnica', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      api_key: values.api_key ?? '',
    }))
    .with('sonar', tool => {
      const isSonarCloud = getIsSonarCloudUrl(values.base_uri)
      return {
        type: tool,
        name,
        base_uri: values.base_uri ?? '',
        token: values.token ?? '',
        organization: isSonarCloud ? values.organization : undefined,
        github_app_id: isSonarCloud ? undefined : parsePositiveInteger(values.github_app_id),
        webhook_secret: omitIfBlank(values.webhook_secret),
      }
    })
    .with('polaris', tool => ({ type: tool, name, base_uri: values.base_uri ?? '', api_token: values.api_token ?? '' }))
    .with('black-duck-hub', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      api_token: values.api_token ?? '',
    }))
    .with('checkmarx-enterprise', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      username: values.username ?? '',
      password: values.password ?? '',
      client_id: values.client_id ?? '',
      client_secret: values.client_secret ?? '',
    }))
    .with('veracode', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      api_key_id: values.api_key_id ?? '',
      api_key_secret: values.api_key_secret ?? '',
      signing_target: omitIfBlank(values.signing_target),
    }))
    .with('checkmarx', tool => ({
      type: tool,
      name,
      region: toCheckmarxRegion(values.region),
      tenant: values.tenant ?? '',
      api_key: values.api_key ?? '',
    }))
    .with('appscan', tool => ({
      type: tool,
      name,
      base_uri: values.base_uri ?? '',
      api_key_id: values.api_key_id ?? '',
      api_key_secret: values.api_key_secret ?? '',
      // The method names which credential the platform will accept and rejects the other one's, so
      // it travels with the credential rather than being inferred from which field is filled.
      // Submit is held until one method is complete, so there is always exactly one to name.
      webhook_auth_method: getWebhookAuthMethod(webhookMethod),
      webhook_secret: webhookMethod === 'secret' ? values.webhook_secret : undefined,
      webhook_user: webhookMethod === 'basic' ? values.webhook_user : undefined,
      webhook_password: webhookMethod === 'basic' ? values.webhook_password : undefined,
    }))
    .with('gitlab', provider => ({
      type: provider,
      name,
      base_uri: values.base_uri ?? '',
      token: values.token ?? '',
      // Omitted rather than sent empty: the platform refuses a blank secret, because a connection
      // that appears to verify deliveries and does not is worse than one that plainly does not.
      webhook_secret: omitIfBlank(values.webhook_secret),
    }))
    .with('bitbucket', provider => ({
      type: provider,
      name,
      username: values.username ?? '',
      email_address: values.email_address ?? '',
      api_token: values.api_token ?? '',
    }))
    .with('azure', provider => ({
      type: provider,
      name,
      base_uri: values.base_uri ?? '',
      organization: values.organization ?? '',
      // The platform takes exactly one method's credentials, so the others are left out entirely.
      // Switching methods clears the abandoned ones too, but the body does not rely on that.
      token: azureAuthMethod === 'pat' ? (values.token ?? '') : undefined,
      managed_identity_client_id:
        azureAuthMethod === 'managed-identity' ? (values.managed_identity_client_id ?? '') : undefined,
      // A work item ID is digits in a text field, so an unparseable entry is dropped rather than
      // sent as NaN. The field is optional, and the form's own validation is what should catch it.
      work_item_id: parseWorkItemId(values.work_item_id),
      commit_message_prefix: omitIfBlank(values.commit_message_prefix),
      webhook_password: omitIfBlank(values.webhook_password),
    }))
    .with('github', provider => ({
      type: provider,
      name,
      base_uri: values.base_uri ?? '',
      app_id: values.app_id ?? '',
      app_name: values.app_name ?? '',
      private_key: values.private_key ?? '',
      webhook_secret: values.webhook_secret ?? '',
    }))
    .exhaustive()
}

// #endregion

export const SetupDrawer: React.FC<SetupDrawerProps> = ({ entry, onClose, opener }) => {
  const tool = entry.addableType
  const { integrations } = useAllIntegrations()
  const hasSystemConnection = integrations.some(
    integration => getIsSystemInstance(integration) && integration.type === tool
  )
  const systemConnectionNote =
    hasSystemConnection && tool !== undefined && getHasConnectionWebhookRoute(tool)
      ? getSystemConnectionWebhookNote({ type: tool, toolLabel: entry.label })
      : undefined
  const form = useSetupForm({ values: initialValuesFor(tool ? getToolFields({ tool, baseUri: undefined }) : []) })
  const drawerContentRef = useRef<HTMLDivElement>(null)
  const step = useSelector(form.store, state => state.values.step)
  const savedInstance = useSelector(form.store, state => state.values.savedInstance)
  // Scopes the create to this drawer, so a create still running from a Setup drawer that has since
  // closed does not block Escape in this one.
  const callerId = useId()
  const getIsCreating = useGetIsPostIntegrationMutating({ callerId })

  return (
    <Dialog.Root open modal={false} onOpenChange={open => (!open ? onClose() : undefined)}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <form.AppForm>
          <Dialog.Content
            ref={drawerContentRef}
            className={classNames(styles.content, footerStyles.drawerContentPaddingAdjustment)}
            data-has-floating-footer={true}
            onInteractOutside={event => event.preventDefault()}
            onCloseAutoFocus={returnFocusOnClose(opener)}
            onEscapeKeyDown={preventEscapeWhile(getIsCreating)}
          >
            <div className={styles.header}>
              <Dialog.Title className={styles.titleRow}>
                <span className={styles.title}>Integrate with</span>
                <CatalogBrandIcon icon={entry.icon} />
                <span className={styles.title}>{entry.label}</span>
              </Dialog.Title>
              <Dialog.Close asChild>
                <button type="button" className={styles.closeButton} aria-label="Close">
                  <XIcon size={24} weight="regular" />
                </button>
              </Dialog.Close>
            </div>

            {step === 'handoff' && savedInstance ? (
              <WebhookHandoff
                toolLabel={entry.label}
                instanceName={savedInstance.name}
                webhookUrl={savedInstance.webhookUrl}
                subject={savedInstance.subject}
                systemConnectionNote={systemConnectionNote}
              />
            ) : (
              <SetupFields tool={tool} />
            )}

            <VisuallyHidden>
              <Dialog.Description>Enter your {entry.label} credentials to connect a new instance.</Dialog.Description>
            </VisuallyHidden>
          </Dialog.Content>

          <FloatingFooter drawerContentRef={drawerContentRef} width="standard">
            <SetupFooter entry={entry} callerId={callerId} onClose={onClose} />
          </FloatingFooter>
        </form.AppForm>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// #region Field leaves

type SetupFieldsProps = { tool: CreatableIntegrationType | undefined }

const SetupFields: React.FC<SetupFieldsProps> = ({ tool }) => {
  const form = useFormContext()
  const name = useSelector(form.store, state => state.values.name)
  const values = useSelector(form.store, state => state.values.values)
  const saveError = useSelector(form.store, state => state.values.saveError)
  const azureAuthChoice = useAzureAuthChoice({ tool, baseUri: values.base_uri })
  const fields = getSetupFields({ tool, baseUri: values.base_uri, azureAuthMethod: azureAuthChoice.method })
  // With no managed identity to offer there is nothing to choose, so the token stays in the plain
  // field list and the form is the one it has always been.
  const azureCredentialFields = azureAuthChoice.isOffered ? AZURE_AUTH_FIELDS[azureAuthChoice.method] : []

  const setValue = (key: string, value: string) =>
    form.setFieldValue('values', previous => ({ ...previous, [key]: value }))

  // Azure's credential fields render inside its method chooser rather than in the plain list.
  const mainFields = fields.filter(field => !field.isAdvanced && !azureCredentialFields.includes(field))
  const advancedFields = fields.filter(field => field.isAdvanced)
  const advancedSummary = tool ? ADVANCED_SECTION_SUMMARY[tool] : undefined
  // Nothing is stored on a create, so the only thing that opens this section by itself is a server
  // error anchored inside it.
  const hasAdvancedError = advancedFields.some(field => getIsFieldAnchored(saveError, field.key))
  // A malformed value disables "Test and connect", so the section holding it stays open: collapsed,
  // it would leave a disabled button with nothing on screen explaining why.
  const hasAdvancedFormatError = advancedFields.some(
    field => getFieldFormatError(field, values[field.key]) !== undefined
  )

  const renderField = (field: IntegrationField, options?: { value: string; label: string }[]) => (
    <IntegrationFieldInput
      key={field.key}
      field={field}
      id={`integration-${field.key}`}
      value={values[field.key] ?? ''}
      onChange={value => setValue(field.key, value)}
      errorText={getFieldErrorText(saveError, field.key)}
      isError={getIsFieldAnchored(saveError, field.key)}
      options={options}
    />
  )

  const azureAuthFieldset = azureAuthChoice.isOffered ? (
    <SetupAzureAuthFieldset
      key="azure-auth"
      method={azureAuthChoice.method}
      fields={azureCredentialFields}
      managedIdentityClientIds={azureAuthChoice.managedIdentityClientIds}
      renderField={renderField}
    />
  ) : null

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionHeading}>Connect your instance</h3>
      {saveError?.formMessage && <FormErrorBanner message={saveError.formMessage} />}
      <div className={styles.fields}>
        {/* Required by the create contract (`minLength: 1`), and it is what tells two instances of
            the same tool apart everywhere they are listed. */}
        <TextInput
          id="integration-name"
          label="Name"
          isRequired
          value={name}
          onChange={value => form.setFieldValue('name', value)}
          placeholder="Name this integration"
          helperText="How this instance is identified across Pixee."
          errorText={getFieldErrorText(saveError, NAME_FIELD_KEY)}
          isError={getIsFieldAnchored(saveError, NAME_FIELD_KEY)}
        />
        {mainFields.flatMap(field =>
          // Right after Organization, so the method sits with the connection it authenticates and
          // ahead of the optional commit settings.
          field.key === AZURE_AUTH_ANCHOR_KEY ? [renderField(field), azureAuthFieldset] : [renderField(field)]
        )}
        {/* Never dropped for want of its anchor: its fields are required, so losing them would hold
            "Test and connect" with nothing on screen to fill in. */}
        {!mainFields.some(field => field.key === AZURE_AUTH_ANCHOR_KEY) && azureAuthFieldset}
        {tool === 'appscan' && <SetupWebhookFieldset />}
        {advancedSummary && advancedFields.length > 0 && (
          <Disclosure summary={advancedSummary} defaultOpen={hasAdvancedError} isForcedOpen={hasAdvancedFormatError}>
            {advancedFields.map(field => renderField(field))}
          </Disclosure>
        )}
      </div>
    </div>
  )
}

/** The field Azure DevOps's method chooser follows. */
const AZURE_AUTH_ANCHOR_KEY = 'organization'

type SetupAzureAuthFieldsetProps = {
  method: AzureAuthChoice
  fields: IntegrationField[]
  managedIdentityClientIds: string[]
  renderField: (field: IntegrationField, options?: { value: string; label: string }[]) => React.ReactNode
}

const AZURE_AUTH_DESCRIPTION_ID = 'azure-auth-description'

const SetupAzureAuthFieldset: React.FC<SetupAzureAuthFieldsetProps> = ({
  method: currentMethod,
  fields,
  managedIdentityClientIds,
  renderField,
}) => {
  const form = useFormContext()
  const identityOptions = managedIdentityClientIds.map(clientId => ({ value: clientId, label: clientId }))

  return (
    <fieldset className={styles.webhookAuthFieldset}>
      <legend className={styles.sectionHeading}>Authentication</legend>
      <p id={AZURE_AUTH_DESCRIPTION_ID} className={styles.webhookAuthLegend}>
        How Pixee authenticates to Azure DevOps. It cannot be changed once the connection is saved.
      </p>
      <RadioGroup
        className={styles.webhookAuthOptions}
        aria-describedby={AZURE_AUTH_DESCRIPTION_ID}
        value={currentMethod}
        onValueChange={value => {
          const method = toAzureAuthMethod(value)
          form.setFieldValue('azureAuthMethod', method)
          // A failure reported for the other method says nothing about this one.
          form.setFieldValue('saveError', undefined)
          // Only the chosen method's credentials are sent, so the others are cleared rather than
          // left behind where a secret typed and abandoned would sit in the form unseen. A single
          // identity is the only one there is to choose, so it is chosen for the user.
          const cleared = Object.fromEntries(
            Object.values(AZURE_AUTH_FIELDS).flatMap(group => group.map(field => [field.key, '']))
          )
          const preselected =
            method === 'managed-identity' && managedIdentityClientIds.length === 1
              ? (managedIdentityClientIds[0] ?? '')
              : ''
          form.setFieldValue('values', previous => ({
            ...previous,
            ...cleared,
            [AZURE_MANAGED_IDENTITY_FIELD_KEY]: preselected,
          }))
        }}
      >
        <RadioItem id="azure-auth-pat" value="pat" label="Personal access token" />
        <RadioItem id="azure-auth-managed-identity" value="managed-identity" label="Managed identity" />
      </RadioGroup>
      {fields.map(field => renderField(field, field.kind === 'select' ? identityOptions : undefined))}
    </fieldset>
  )
}

const WEBHOOK_AUTH_DESCRIPTION_ID = 'webhook-auth-description'

const SetupWebhookFieldset: React.FC = () => {
  const form = useFormContext()
  const webhookMethod = useSelector(form.store, state => state.values.webhookMethod)
  const values = useSelector(form.store, state => state.values.values)
  const setValue = (key: string, value: string) =>
    form.setFieldValue('values', previous => ({ ...previous, [key]: value }))

  return (
    <fieldset className={styles.webhookAuthFieldset}>
      <legend className={styles.sectionHeading}>Webhook authentication</legend>
      <p id={WEBHOOK_AUTH_DESCRIPTION_ID} className={styles.webhookAuthLegend}>
        How AppScan proves to Pixee that a webhook call is genuine. You will configure the same method in AppScan.
      </p>
      <RadioGroup
        className={styles.webhookAuthOptions}
        aria-describedby={WEBHOOK_AUTH_DESCRIPTION_ID}
        value={webhookMethod}
        onValueChange={value => {
          const method = toWebhookAuthMethod(value)
          form.setFieldValue('webhookMethod', method)
          // Exactly one method is submitted, so the other's values are dead once the user switches.
          // Cleared rather than left behind, so nothing the user typed and abandoned can be read back
          // — the handoff step's "was a secret set?" question reads this same bag.
          form.setFieldValue('values', previous => ({
            ...previous,
            webhook_secret: method === 'secret' ? (previous.webhook_secret ?? '') : '',
            webhook_user: method === 'basic' ? (previous.webhook_user ?? '') : '',
            webhook_password: method === 'basic' ? (previous.webhook_password ?? '') : '',
          }))
        }}
      >
        <RadioItem id="webhook-method-secret" value="secret" label="Shared secret" />
        <RadioItem id="webhook-method-basic" value="basic" label="Basic auth" />
      </RadioGroup>
      {match(webhookMethod)
        .with('secret', () => (
          <TextInput
            id="integration-webhook_secret"
            label="Webhook secret"
            type="password"
            isRequired
            value={values.webhook_secret}
            onChange={value => setValue('webhook_secret', value)}
          />
        ))
        .with('basic', () => (
          <>
            <TextInput
              id="integration-webhook_user"
              label="Webhook username"
              isRequired
              value={values.webhook_user}
              onChange={value => setValue('webhook_user', value)}
            />
            <TextInput
              id="integration-webhook_password"
              label="Webhook password"
              type="password"
              isRequired
              value={values.webhook_password}
              onChange={value => setValue('webhook_password', value)}
            />
          </>
        ))
        .exhaustive()}
    </fieldset>
  )
}

// #endregion

const getIsFilled = (value: string | undefined): boolean => (value ?? '').trim() !== ''

const getWebhookHandoff = ({
  tool,
  response,
  formValues,
}: {
  tool: CreatableIntegrationType | undefined
  response: unknown
  formValues: SetupFormValues
}): Pick<SavedInstance, 'webhookUrl' | 'subject'> | undefined => {
  if (tool === undefined) {
    return undefined
  }
  if (getIsWebhookTool(tool)) {
    const webhookUrl = getWebhookUrl(response)
    if (webhookUrl === undefined) {
      return undefined
    }
    const subject: WebhookHandoffSubject = match(tool)
      .with('sonar', type => ({ type, hasWebhookSecret: getIsFilled(formValues.values.webhook_secret) }))
      .with('appscan', type => ({ type, webhookAuthChoice: formValues.webhookMethod }))
      .exhaustive()
    return { webhookUrl, subject }
  }
  if (getHasConnectionWebhookRoute(tool)) {
    const subject: WebhookHandoffSubject | undefined = match(tool)
      .with('github', type => ({ type }))
      .with('gitlab', type => (getIsFilled(formValues.values.webhook_secret) ? { type } : undefined))
      .with('azure', type =>
        getIsFilled(formValues.values.webhook_password)
          ? { type, webhookUser: getAzureWebhookUser(response) }
          : undefined
      )
      .exhaustive()
    const id = getCreatedId(response)
    return subject === undefined || id === undefined
      ? undefined
      : { webhookUrl: getConnectionWebhookUrl({ type: tool, id }), subject }
  }
  return undefined
}

// #region Footer leaf

type SetupFooterProps = { entry: CatalogEntry; callerId: string; onClose: () => unknown }

const SetupFooter: React.FC<SetupFooterProps> = ({ entry, callerId, onClose }) => {
  const form = useFormContext()
  const tool = entry.addableType
  const name = useSelector(form.store, state => state.values.name)
  const values = useSelector(form.store, state => state.values.values)
  const azureAuthChoice = useAzureAuthChoice({ tool, baseUri: values.base_uri })
  const fields = getSetupFields({ tool, baseUri: values.base_uri, azureAuthMethod: azureAuthChoice.method })
  const webhookMethod = useSelector(form.store, state => state.values.webhookMethod)
  const step = useSelector(form.store, state => state.values.step)

  const { addToast } = useToastService()
  const [docsOpener, setDocsOpener] = useState<HTMLElement>()

  // No `409` override: `POST /api/v1/integrations` answers a duplicate name and an already-connected
  // GitHub App under the same status, so one keyed on the status alone asserted the name was at
  // fault whichever it was — and contradicted the App id field that now says otherwise. A conflict
  // anchored to a field is not toasted at all; anything else carries the platform's own words.
  const showConnectError = useShowMutationError({ prefix: 'Failed to connect integration' })
  const postIntegration = usePostIntegration({
    callerId,
    onSuccess: data => {
      addToast({ message: <>{entry.label} integration added.</>, variant: 'success' })
      const handoff = getWebhookHandoff({ tool, response: data, formValues: form.state.values })
      if (handoff === undefined) {
        onClose()
        return
      }
      form.setFieldValue('savedInstance', { name: form.state.values.name.trim(), ...handoff })
      form.setFieldValue('saveError', undefined)
      form.setFieldValue('step', 'handoff')
    },
    onError: (...args) => {
      const [error] = args
      if (reportRoleRefusal({ error, message: <>You do not have permission to add integrations.</>, addToast })) {
        return
      }
      // Decides which field the drawer marks, so the user is pointed at the input they can act on
      // rather than at the form as a whole.
      const saveError = getIntegrationSaveError({
        error,
        fields,
        values: form.state.values.values,
        toolLabel: tool ? getProviderLabel(tool) : entry.label,
        operation: 'create',
      })
      form.setFieldValue('saveError', saveError)
      // A conflict the field already explains is not repeated in a toast — two reports of one
      // refusal disagree as soon as they name different causes, which is how an already-connected
      // App came to be reported as a duplicate name.
      if (getIsConflictReportedOnField(error, saveError)) {
        return
      }
      showConnectError(...args)
    },
  })

  if (step === 'handoff') {
    return (
      <div className={styles.footerActions}>
        <DefaultButton buttonType="primary" onClick={onClose}>
          Done
        </DefaultButton>
      </div>
    )
  }

  const isAppScan = tool === 'appscan'
  const areRequiredFieldsFilled = fields.every(
    field => !getIsFieldRequired(field) || (values[field.key] ?? '').trim() !== ''
  )
  // Two different failures, held the same way: a malformed URL fails the request schema, and a work
  // item ID that will not parse is dropped from the body instead, which creates the connection
  // without the reference the user thought they had named.
  const areFieldFormatsValid = fields.every(field => getFieldFormatError(field, values[field.key]) === undefined)
  const isWebhookFilled = match(webhookMethod)
    .with('secret', () => (values.webhook_secret ?? '').trim() !== '')
    .with('basic', () => (values.webhook_user ?? '').trim() !== '' && (values.webhook_password ?? '').trim() !== '')
    .exhaustive()
  const isSubmitDisabled =
    tool === undefined ||
    name.trim() === '' ||
    !areRequiredFieldsFilled ||
    !areFieldFormatsValid ||
    (isAppScan && !isWebhookFilled)

  const handleSubmit = () => {
    if (tool === undefined) {
      return
    }
    form.setFieldValue('saveError', undefined)
    postIntegration.mutate({
      body: buildCreateBody(tool, { ...form.state.values, azureAuthMethod: azureAuthChoice.method }),
    })
  }

  return (
    <>
      <button
        type="button"
        className={styles.docsTrigger}
        onClick={event => {
          const trigger = event.currentTarget
          setDocsOpener(prev => (prev ? undefined : trigger))
        }}
      >
        <span className={styles.docsTriggerIcon}>
          <QuestionIcon size={20} weight="regular" />
        </span>
        View documentation
      </button>
      <div className={styles.footerActions}>
        {/* Cancelling mid-create would leave the drawer guessing whether the instance was made, so
            the connect in flight owns the footer until it settles. */}
        <GhostButton type="secondary" state={postIntegration.isPending ? 'disabled' : 'default'} onClick={onClose}>
          Cancel
        </GhostButton>
        <DefaultButton
          buttonType="primary"
          state={postIntegration.isPending ? 'loading' : isSubmitDisabled ? 'disabled' : 'default'}
          onClick={handleSubmit}
          loadingLabel="Testing"
        >
          Test and connect
        </DefaultButton>
      </div>
      {docsOpener && (
        <DocumentationDrawer
          entry={entry}
          placement="beside"
          opener={docsOpener}
          onClose={() => setDocsOpener(undefined)}
        />
      )}
    </>
  )
}

// #endregion
